import { appLogger } from '@tn/foundation/utils/app-logger/appLogger';

import { getLegacyFlowCookieContent } from '../helpers';

/**
 * graph.svod flow-cookie type legacy sets for the agnostic rendezvous flow
 * (web-allaccess Flow::TYPE_AGNOSTIC_RENDEZVOUS — the legacy SPA at /tv in
 * US and /xx/tv in INTL). The 'tn-rendezvous' type belongs to the TN
 * rendezvous page, which is not ready — those users keep signing in on the
 * legacy page.
 */
export const RENDEZVOUS_FLOW_TYPES = ['agnostic-rendezvous'] as const;
export type RendezvousFlowType = (typeof RENDEZVOUS_FLOW_TYPES)[number];

/**
 * sessionStorage flag legacy RendezvousContext.storeActivationScreenSignIn()
 * writes for sign-in-only devices before redirecting to sign-in. It is a
 * FlowModel constant on the legacy side, never a cookie flow type.
 */
const SIGN_IN_ONLY_SESSION_KEY = 'overrideFlowType';
const SIGN_IN_ONLY_SESSION_VALUE = 'agnostic-rendezvous-sign-in-only';

export type RendezvousSignInHandoff = {
    flowType: RendezvousFlowType;
    /**
     * The device only supports signing in on web (lookup response
     * activationScreen === 'SIGN_IN') — hide the sign-up and MVPD CTAs on the
     * sign-in page (legacy UserEntry.displaySignUpLink / SignIn.showMVPDCtaIntl
     * parity).
     */
    signInOnly: boolean;
};

/**
 * Rendezvous users sign in on the default sign-in page: always in INTL,
 * and in US for sign-in-only devices (SIGN_UP-capable US devices stay
 * inside the rendezvous SPA at /tv/signin — out of scope). A rendezvous
 * sign-in is recognized by the legacy graph.svod flow cookie — the
 * authoritative signal (10h TTL, validated server-side against the ott
 * code cookie). Returns null when this sign-in is not a rendezvous handoff.
 */
export const getRendezvousSignInHandoff = (): RendezvousSignInHandoff | null => {
    let type: string | undefined;
    try {
        type = getLegacyFlowCookieContent()?.type as string | undefined;
    } catch (err) {
        appLogger.warn(
            'getRendezvousSignInHandoff: flow cookie read failed, treating as no handoff',
            {
                error: String(err),
            },
        );
        return null;
    }
    if (!type || !(RENDEZVOUS_FLOW_TYPES as readonly string[]).includes(type)) {
        return null;
    }
    // The cookie is the authoritative signal — a blocked sessionStorage must
    // not discard the handoff, only the sign-in-only refinement.
    let signInOnly = false;
    try {
        signInOnly =
            window.sessionStorage.getItem(SIGN_IN_ONLY_SESSION_KEY) === SIGN_IN_ONLY_SESSION_VALUE;
    } catch (err) {
        appLogger.warn(
            'getRendezvousSignInHandoff: sessionStorage read failed, assuming not sign-in-only',
            {
                error: String(err),
            },
        );
    }
    return { flowType: type as RendezvousFlowType, signInOnly };
};

/**
 * The handoff only matters where the sign-in flow honors it
 * (options.rendezvousTNSignInRedirectEnabled — P+ only). Single predicate
 * for the dtc destination decision, the sign-in-only page UI and tracking:
 * on flag-off brands (PTV) a stray rendezvous cookie must not divert the
 * normal sign-in completion.
 */
export const getEnabledRendezvousSignInHandoff = (
    getConfigByKey: (key: string) => unknown,
): RendezvousSignInHandoff | null =>
    getConfigByKey('options.rendezvousTNSignInRedirectEnabled')
        ? getRendezvousSignInHandoff()
        : null;

/**
 * Reads the handoff the dtc machine detected on signIn entry
 * (detectRendezvousSignInHandoff action) out of a flow snapshot. Components
 * stay dumb: they consume machine state through this selector instead of
 * touching cookies/storage/config themselves.
 */
export const selectRendezvousSignInHandoff = (
    flowState: {
        context?: { rendezvousSignInHandoff?: RendezvousSignInHandoff | null };
    } | null,
): RendezvousSignInHandoff | null => flowState?.context?.rendezvousSignInHandoff ?? null;

/**
 * Legacy SignIn.vue clears the sign-in-only flag on any successful sign-in
 * (RendezvousContext.clearStoredActivationScreen) so later organic sign-ins
 * render the full page again.
 */
export const clearRendezvousSignInOnlyFlag = (): void => {
    try {
        window.sessionStorage.removeItem(SIGN_IN_ONLY_SESSION_KEY);
    } catch (err) {
        // storage unavailable — nothing to clear, but distinguishable in diagnostics
        appLogger.warn('clearRendezvousSignInOnlyFlag: sessionStorage unavailable', {
            error: String(err),
        });
    }
};
